import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { NavLink } from "react-router-dom";
import { useRecoilState } from "recoil";
import { showMenu, aboutUs } from "@atoms";
import { useSecondModal } from "@helpers/hooks/useSecondModal";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import { trackB2BCtaClick } from "@helpers/linkedinTracking";
import MenuOverlay from "@shared/MenuOverlay";
import "./style.css";

// Business logos
import liquidityLogo from "./businessLogos/Liquidity Wordmark Black RGB.svg";
import eitanLogo from "./businessLogos/איתן-מדיקל-לוגו-לאתר-כתיבה-טכנית-כתב-טכני-מקצועי-צבי-כתב.png";
import rLogo from "./businessLogos/R.png";

// Customer testimonial videos from stories/thank you page
const testimonialVideos = [
  "https://d5l8cb2ud43ux.cloudfront.net/A74230C00196A65E1BEC05A8CB7AC8A0_video_dashinit_wthf6e.webm",
  "https://d5l8cb2ud43ux.cloudfront.net/video-output-25D4A12B-FFDF-43E5-8DA8-87DD98830949_kzoinx.webm",
  "https://d5l8cb2ud43ux.cloudfront.net/Untitled_video_-_Made_with_Clipchamp_3_bgzbnw.webm",
  "https://d5l8cb2ud43ux.cloudfront.net/mothers_sapirbengozi_nvqr4m.webm",
  "https://d5l8cb2ud43ux.cloudfront.net/Untitled_video_-_Made_with_Clipchamp_2_sgpwl9.webm",
];

// Doors - Use cases for business gifts
const useCaseDoors = [
  {
    id: "holidays",
    title: "חוגגים את החג",
    description: "יום המשפחה, ראש השנה, פסח, חנוכה - מתנה מושלמת לכל חג",
  },
  {
    id: "experience",
    title: "בא לנו להעניק",
    description: "הוקרה, ימי הולדת, קליטת עובדים חדשים ואירועי צוות",
  },
];

// Q&A items
const faqItems = [
  {
    question: "האם צריך לקדוח או להשתמש במסמרים?",
    answer: "לא. כל המוצרים של Blends נתלים ללא מסמרים וללא קידוחים, ומתאימים לקירות בבית או בדירה שכורה.",
  },
  {
    question: "איך מתבצע תהליך ההזמנה לחברה?",
    answer: "פשוט וקל: יוצרים קשר לקבלת הצעת מחיר, בוחרים כמות ומיתוג (אם יש), אנחנו מטפלים בייצור, אריזה ומשלוח. בלי לוגיסטיקה מסובכת ובלי כאב ראש ל-HR.",
  },
  {
    question: "האם ניתן להזמין בכמויות?",
    answer: "בוודאי. Blends מתמחה בהזמנות לחברות – מעשרות ועד מאות עובדים.",
  },
  {
    question: "האם אפשר להוסיף מיתוג של החברה?",
    answer: "כן. ניתן לשלב מיתוג עדין לפי הצורך, או לשמור על מראה נקי ואלגנטי ללא מיתוג – לבחירתכם.",
  },
  {
    question: "תוך כמה זמן ההזמנה מוכנה?",
    answer: "ההזמנה מגיעה בהתאם לזמנים המופיעים בעגלה בזמן הרכישה, וברוב המקרים נעשה את המיטב כדי שההזמנה תגיע אליכם בתוך 3-7 ימי עסקים. למתנות לחגים – מומלץ להזמין מראש.",
  },
];

const BusinessGiftsHello = () => {
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [menuShow, setMenuShow] = useRecoilState(showMenu);
  const [, setAboutUs] = useRecoilState(aboutUs);
  const modal = useSecondModal();
  const { width } = useWindowDimensions();
  const heroCtaRef = useRef(null);

  // Pricing calculator state
  const [employeeCount, setEmployeeCount] = useState(20);
  const [framesPerEmployee, setFramesPerEmployee] = useState(3);

  // Price calculation
  const pricePerFrame = 49; // Base price per frame
  const calculatedPrice = useMemo(() => {
    const totalFrames = employeeCount * framesPerEmployee;
    let discount = 0;
    if (totalFrames >= 100) discount = 0.35;
    else if (totalFrames >= 50) discount = 0.30;
    else if (totalFrames >= 20) discount = 0.20;
    else if (totalFrames >= 10) discount = 0.10;

    const basePrice = totalFrames * pricePerFrame;
    const discountedPrice = Math.round(basePrice * (1 - discount));
    return {
      totalFrames,
      basePrice,
      discount: Math.round(discount * 100),
      discountedPrice,
      pricePerFrame: Math.round(pricePerFrame * (1 - discount))
    };
  }, [employeeCount, framesPerEmployee]);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleAboutUsModal = (type) => {
    setAboutUs(type);
    if (width > 768) {
      modal("open", "aboutUs");
    } else {
      modal("open", "aboutUsMobile");
    }
  };

  // Show sticky CTA when hero CTA scrolls out of view
  useEffect(() => {
    const handleScroll = () => {
      if (heroCtaRef.current) {
        const rect = heroCtaRef.current.getBoundingClientRect();
        // Show sticky when hero CTA is above viewport
        setShowStickyCta(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const whatsappLink = "https://api.whatsapp.com/send?phone=972504993895&text=היי+הגעתי+דרך+לינקדאין+ואני+רוצה+להזמין+מתנות+לעובדים";

  const handleDoorClick = (door) => {
    trackB2BCtaClick('door-' + door.id);
    const text = encodeURIComponent(`היי, אני מעוניין/ת במתנות לעובדים עבור ${door.title}`);
    window.open(`https://api.whatsapp.com/send?phone=972504993895&text=${text}`, "_blank");
  };

  const handleCtaClick = (location) => {
    trackB2BCtaClick(location);
  };

  return (
    <div className="business-gifts-hello-page" dir="rtl">
      {/* Header */}
      <div className="hello-header-wrapper">
        <header className="hello-header">
          <div className="hello-header-menu" onClick={() => setMenuShow(true)}>
            <img src="/assets/images/bar.svg" alt="תפריט" />
          </div>
          <NavLink to="/">
            <img src="/assets/images/stk_logo.svg" alt="Blends" className="hello-logo" />
          </NavLink>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hello-header-chat"
          >
            <img src="/assets/images/wtsap.svg" alt="WhatsApp" />
          </a>
        </header>
      </div>
      {/* Menu - rendered via portal to ensure fixed positioning */}
      {ReactDOM.createPortal(
        <>
          <MenuOverlay isVisible={menuShow} />
          <div className="menu hello-menu" style={{ right: menuShow ? '0' : '-100%' }}>
            <div className="menu-head text-center">
              <img src="/assets/images/stk_logo.svg" alt="בלנדס"/>
            </div>
            <div className="menu-items">
              <ul style={{ display: "grid", direction: "rtl"}}>
                <li style={{marginBottom: "0px"}} onClick={() => {
                  setMenuShow(false);
                  const paModal = width > 767 ? 'personalArea' : 'personalAreaMobile';
                  modal('open', paModal);
                }}>
                  <span className="menu-icon-wrap menu-icon-wrap--1 menu-icon-wrap--notif">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#087E63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <span className="menu-link--personal" style={{cursor: 'pointer'}}>אזור אישי</span>
                </li>
                <li style={{marginBottom: "0px"}}>
                  <span className="menu-icon-wrap menu-icon-wrap--2">
                    <img src="/assets/images/inmenuwtsap.png" alt="" />
                  </span>
                  <span onClick={() => {
                    modal('open', 'whatsApp');
                  }}>דברו איתנו בוואצאפ</span>
                </li>
                <li style={{marginBottom: "0px"}} onClick={() => handleAboutUsModal('CQ')}>
                  <span className="menu-icon-wrap menu-icon-wrap--3">
                    <img src="/assets/images/inmenufaq.png" alt="" />
                  </span>
                  <span>שאלות נפוצות</span>
                </li>
                <li className="in-menu-howto" style={{marginBottom: "0px"}} onClick={() => handleAboutUsModal('HT')}>
                  <span className="menu-icon-wrap menu-icon-wrap--4">
                    <img src="/assets/images/inmenuhowto.png" alt="" />
                  </span>
                  <span>כיצד לרכוש בבלנדס</span>
                </li>
                <li style={{marginBottom: "0px"}}>
                  <span className="menu-icon-wrap menu-icon-wrap--5">
                    <img src="/assets/images/inmenu-gift.png" alt="" />
                  </span>
                  <NavLink to="/business-gifts/hello" onClick={() => setMenuShow(false)} style={{color: 'inherit', textDecoration: 'none'}}>
                    <span>מתנות לעובדים</span>
                  </NavLink>
                </li>
                <li className="menu-divider" aria-hidden="true"></li>
                <li style={{marginBottom: "0px"}}>
                  <span className="menu-icon-wrap menu-icon-wrap--6">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#087E63" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="8" width="18" height="12" rx="2.5"/>
                      <line x1="12" y1="8" x2="12" y2="20"/>
                      <path d="M12 8c0-2.2-1-3.5-2.6-3.5-1.1 0-1.8.8-1.8 1.7C7.6 7.4 9.2 8 12 8Z"/>
                      <path d="M12 8c0-2.2 1-3.5 2.6-3.5 1.1 0 1.8.8 1.8 1.7C16.4 7.4 14.8 8 12 8Z"/>
                    </svg>
                  </span>
                  <NavLink to="/gift-card" onClick={() => setMenuShow(false)} style={{color: 'inherit', textDecoration: 'none'}}>
                    <span>רכישת גיפט קארד</span>
                  </NavLink>
                </li>
                <li style={{marginBottom: "0px"}}>
                  <span className="menu-icon-wrap menu-icon-wrap--7">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#087E63" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                      <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                      <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                      <rect x="14" y="14" width="7" height="7" rx="1.5"/>
                    </svg>
                  </span>
                  <NavLink to="/products" onClick={() => setMenuShow(false)} style={{color: 'inherit', textDecoration: 'none'}}>
                    <span>קטלוג קירות</span>
                  </NavLink>
                </li>
              </ul>
            </div>
            <div className="in-menu-button">
              <NavLink to='/upload-your-image'>
                <button onClick={() => setMenuShow(false)}>עצבו קיר משלכם</button>
              </NavLink>
            </div>
            <div className="in-menu-social">
              <p>עקבו אחרינו</p>
            </div>
            <div className="in-menu-social-icons">
              <a href="https://www.facebook.com/profile.php?id=100086212847385" target="_blank" rel="noreferrer">
                <img src="/assets/images/inmenufacebook.png" alt="הפייסבוק של בלנדס"/>
              </a>
              <a href="https://www.instagram.com/blends_il/" target="_blank" rel="noreferrer">
                <img src="/assets/images/inmenuinstagram.png" alt="האינסטגרם של בלנדס"/>
              </a>
              <a href="https://www.tiktok.com/@blends_il" target="_blank" rel="noreferrer">
                <img src="/assets/images/inmenutiktok.png" alt="הטיקטוק של בלנדס"/>
              </a>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: "20.5px",
                textAlign: "center",
                width: "360px",
                paddingRight: "5.5em"
              }}
            >
              <span style={{fontSize: "14px", color: "#C8C8C8", fontWeight: "300", lineHeight: "36px", textDecorationLine: "underline"}} onClick={() => handleAboutUsModal('AS')}>הצהרת נגישות</span>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <span style={{fontSize: "14px", color: "#C8C8C8", fontWeight: "300", lineHeight: "36px", textDecorationLine: "underline"}} onClick={() => handleAboutUsModal('PP')}>מדיניות פרטיות</span>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Hero Section - New Layout */}
      <section className="hello-hero-new">
        <div className="hello-hero-inner">
          {/* Badge */}
          <span className="hello-badge">מתנות לעובדים</span>

          {/* Headline */}
          <h1 className="hello-headline">
            <span className="hello-headline-bold">תהפכו כל אירוע</span>
            <br />
            <span className="hello-headline-italic">לזיכרון שנשאר על הקיר</span>
          </h1>

          {/* Subtitle */}
          <p className="hello-subtitle">
            מסגרות תמונות איכותיות שנדבקות לקיר - המתנה המושלמת לעובדים שרוצים להנציח את הרגעים היפים שלהם
          </p>

          {/* CTA + Social Proof Row */}
          <div className="hello-cta-row" ref={heroCtaRef}>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hello-cta-btn"
              onClick={() => handleCtaClick('hero')}
            >
              דברו איתנו
            </a>
            <div className="hello-social-proof">
              <span className="hello-proof-number">10K+</span>
              <span className="hello-proof-text">עשרות אלפי תמונות כבר על הקיר</span>
            </div>
          </div>
        </div>

        {/* Video on the right */}
        <div className="hello-cta-video">
          <video
            autoPlay
            loop
            muted
            playsInline
          >
            <source
              type="video/webm"
              src="https://d5l8cb2ud43ux.cloudfront.net/Homepage_Background_Video_enzxjh.webm"
            />
            <source
              type="video/mp4"
              src="https://d5l8cb2ud43ux.cloudfront.net/Homepage_Background_Video_enzxjh.mp4"
            />
          </video>
        </div>

        {/* Hero Benefits */}
        <div className="hello-hero-benefits">
          <span>אריזה לכל עובד</span>
          <span className="hello-benefit-dot">•</span>
          <span>לינק אישי לחברה</span>
          <span className="hello-benefit-dot">•</span>
          <span>משלוח עד אליכם</span>
        </div>
      </section>

      {/* Customers Section */}
      <section className="hello-customers-section">
        <span className="hello-customers-label">מבין לקוחותינו</span>
        <div className="hello-customers-logos">
          <img src={liquidityLogo} alt="Liquidity" />
          <img src={rLogo} alt="Customer" />
          <img src={eitanLogo} alt="Eitan Medical" />
        </div>
      </section>

      {/* Demo Section - How to stick on wall */}
      <section className="hello-demo-section">
        <div className="hello-demo-content">
          <div className="hello-demo-text">
            <h2>קל להדביק על הקיר</h2>
            <p>מסירים את המדבקה מאחור<br />ומדביקים ישירות על הקיר<br />בלי מסמרים, בלי כלים</p>
          </div>
          <div className="hello-demo-video">
            <video
              autoPlay
              loop
              muted
              playsInline
            >
              <source
                type="video/mp4"
                src="/assets/file/videos/HomepageVideoNew.mp4"
              />
              <source
                type="video/webm"
                src="https://d5l8cb2ud43ux.cloudfront.net/HomepageVideoNew_rc6vmu.webm"
              />
            </video>
          </div>
        </div>
      </section>

      {/* Process & Doors Section */}
      <section className="hello-doors-section">
        <div className="hello-process-content">
          <h3>תהליך פשוט, מהיר <span className="hello-emphasis">ומסודר</span></h3>
          <div className="hello-process-steps">
            <div className="hello-process-step">
              <span className="hello-process-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </span>
              <p>בוחרים כמה תמונות כל עובד יקבל</p>
            </div>
            <div className="hello-process-step">
              <span className="hello-process-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
              </span>
              <p>מקבלים לינק שכל עובד מעלה את התמונות דרכו</p>
            </div>
            <div className="hello-process-step">
              <span className="hello-process-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </span>
              <p>מקבלים את המשלוח עד אליכם</p>
            </div>
          </div>
        </div>

        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="hello-doors-cta"
          onClick={() => handleCtaClick('doors-section')}
        >
          דברו איתנו
        </a>

        <div className="hello-doors-content">
          <h2>בואו נבחר יחד את המתנה המושלמת לעובדים</h2>
          <p className="hello-doors-subtitle">בחרו את סוג האירוע ונתאים לכם את הפתרון</p>
          <div className="hello-doors-grid">
            {useCaseDoors.map((door) => (
              <button
                key={door.id}
                className="hello-door-card"
                onClick={() => handleDoorClick(door)}
              >
                <h3>{door.title}</h3>
                <p>{door.description}</p>
                <span className="hello-door-arrow">←</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Carousel */}
      <section className="hello-video-carousel">
        <h2>קירות ורגשות מאת בלנדס</h2>
        <div className="hello-videos-container">
          {testimonialVideos.map((videoUrl, index) => (
            <div key={index} className="hello-video-item">
              <video
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={videoUrl} type="video/webm" />
              </video>
            </div>
          ))}
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="hello-google-section">
        <div className="hello-google-content">
          <img src="/assets/images/google.svg" alt="Google" className="hello-google-logo" />
          <img src="/assets/images/stars.svg" alt="5 כוכבים" className="hello-google-stars" />
          <span className="hello-google-rating">4.8/5</span>
        </div>
        <p className="hello-google-text">מתוך אלפי לקוחות מרוצים</p>
      </section>

      {/* Pricing Calculator Section */}
      <section className="hello-calculator-section">
        <div className="hello-calculator-container">
          <div className="hello-calculator-header">
            <span className="hello-calculator-badge">מחשבון מחירים</span>
            <h2>כמה זה <span className="hello-emphasis">עולה?</span></h2>
            <p className="hello-calculator-subtitle">חשבו במהירות את העלות המשוערת להזמנה שלכם</p>
          </div>

          <div className="hello-calculator-card">
            <div className="hello-calculator-inputs">
              {/* Employee Count */}
              <div className="hello-calc-input-group">
                <label>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  כמות עובדים
                </label>
                <div className="hello-calc-counter">
                  <button
                    onClick={() => setEmployeeCount(Math.max(5, employeeCount - 5))}
                    disabled={employeeCount <= 5}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={employeeCount}
                    onChange={(e) => setEmployeeCount(Math.max(5, Math.min(500, parseInt(e.target.value) || 5)))}
                    min="5"
                    max="500"
                  />
                  <button onClick={() => setEmployeeCount(Math.min(500, employeeCount + 5))}>
                    +
                  </button>
                </div>
                <span className="hello-calc-hint">מינימום 5 עובדים</span>
              </div>

              {/* Frames per Employee */}
              <div className="hello-calc-input-group">
                <label>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  מסגרות לעובד
                </label>
                <div className="hello-calc-frames-select">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      className={framesPerEmployee === num ? "active" : ""}
                      onClick={() => setFramesPerEmployee(num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <span className="hello-calc-hint">מסגרות לכל עובד</span>
              </div>
            </div>

            {/* Price Summary */}
            <div className="hello-calc-summary">
              <div className="hello-calc-summary-row">
                <span>סה״כ מסגרות</span>
                <span className="hello-calc-value">{calculatedPrice.totalFrames}</span>
              </div>
              {calculatedPrice.discount > 0 && (
                <div className="hello-calc-summary-row hello-calc-discount">
                  <span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                      <line x1="7" y1="7" x2="7.01" y2="7"/>
                    </svg>
                    הנחת כמות
                  </span>
                  <span className="hello-calc-discount-value">{calculatedPrice.discount}%−</span>
                </div>
              )}
              <div className="hello-calc-summary-row hello-calc-per-frame">
                <span>מחיר למסגרת</span>
                <span>₪{calculatedPrice.pricePerFrame}</span>
              </div>
              <div className="hello-calc-total">
                <span>סה״כ משוער</span>
                <div className="hello-calc-total-price">
                  {calculatedPrice.discount > 0 && (
                    <span className="hello-calc-original-price">₪{calculatedPrice.basePrice.toLocaleString()}</span>
                  )}
                  <span className="hello-calc-final-price">₪{calculatedPrice.discountedPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="hello-calc-benefits">
              <div className="hello-calc-benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>משלוח עד אליכם כלול</span>
              </div>
              <div className="hello-calc-benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>לינק ייעודי לחברה</span>
              </div>
              <div className="hello-calc-benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>מיתוג אישי אפשרי</span>
              </div>
              <div className="hello-calc-benefit">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>אריזה אישית לכל עובד</span>
              </div>
            </div>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hello-calc-cta"
              onClick={() => handleCtaClick('calculator')}
            >
              דברו איתנו לקבלת הצעת מחיר
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 5 12 12 19"/>
              </svg>
            </a>

            <p className="hello-calc-disclaimer">
              * המחיר הסופי עשוי להשתנות בהתאם לסוג המסגרות והמיתוג הנבחר
            </p>
          </div>
        </div>
      </section>

      {/* Q&A Section */}
      <section className="hello-faq-section">
        <h2>שאלות נפוצות</h2>
        <div className="hello-faq-list">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className={`hello-faq-item ${expandedFaq === index ? "expanded" : ""}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="hello-faq-header">
                <h3>{item.question}</h3>
                <span className="hello-faq-icon">{expandedFaq === index ? "−" : "+"}</span>
              </div>
              <div className="hello-faq-answer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="hello-footer">
        <img src="/assets/images/stk_logo.svg" alt="Blends" className="hello-footer-logo" />
        <p>Blends - מסגרות תמונות שנדבקות לקיר</p>
      </footer>

      {/* Sticky CTA - rendered via Portal to ensure fixed positioning works */}
      {ReactDOM.createPortal(
        <div className={`hello-sticky-cta ${showStickyCta ? "visible" : ""}`}>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hello-sticky-btn"
            onClick={() => handleCtaClick('sticky')}
          >
            דברו איתנו
          </a>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BusinessGiftsHello;
